import { useMutation } from '@tanstack/react-query'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'src/components/button'
import { ErrorMessage } from 'src/components/errors'
import { Input } from 'src/components/input'
import { GuestPinSetup, setupGuestSurveyPin } from 'src/services/guest-survey'
import {
  SurveyPinForm,
  SurveyPinFormInputs,
  surveyPinFormLevelValidators,
  TSurveyPinForm,
} from 'src/services/survey/forms'
import { useSurveyPin } from 'src/stores/survey-pin'

type PinSetupFormProps = {
  instanceId: string
  /** Guest link token: the PIN is set under it and the emailed resume link uses it. */
  token: string
  /** The guest's Contact email, when known. */
  defaultEmail?: string | null
  onDone?: () => void
}

/** Shows the PIN once after setup, with whether it was emailed. */
const PinSetupConfirmation = ({
  pin,
  result,
  onContinue,
}: {
  pin: string
  result: GuestPinSetup
  onContinue: () => void
}) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-4" data-testid="pin-setup-confirmation">
      <h2 className="text-lg font-medium text-text-primary">{t('surveyPin.confirmationTitle')}</h2>
      <p
        className="text-center text-2xl font-medium tracking-widest text-text-primary"
        data-testid="pin-setup-confirmation-pin"
      >
        {pin}
      </p>
      {result.emailSent ? (
        <p className="text-sm text-text-secondary" data-testid="pin-setup-emailed">
          {t('surveyPin.emailedTo', { email: result.email })}
        </p>
      ) : (
        <div
          className="bg-warning/10 rounded-md px-3 py-2 text-sm text-text-primary"
          data-testid="pin-setup-email-failed"
        >
          {t('surveyPin.emailFailed')}
        </div>
      )}
      <Button onClick={onContinue} data-testid="pin-setup-continue">
        {t('surveyPin.continue')}
      </Button>
    </div>
  )
}

const PinSetupFormInner = ({ instanceId, token, defaultEmail, onDone }: PinSetupFormProps) => {
  const { t } = useTranslation()
  const { form, createFormFieldChangeHandler } = useTnForm<TSurveyPinForm>()
  const setPin = useSurveyPin((s) => s.setPin)
  // The plain PIN lives only in component memory until the guest continues.
  const [confirmed, setConfirmed] = useState<{ pin: string; result: GuestPinSetup } | null>(null)

  useEffect(() => {
    if (defaultEmail && !form.email.value) createFormFieldChangeHandler(form.email)(defaultEmail)
    // Prefill once, when the Contact email arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmail])

  const {
    mutate: submitPin,
    isPending,
    isError,
  } = useMutation({
    mutationFn: ({ pin, email }: { pin: string; email: string }) =>
      setupGuestSurveyPin({ token, instanceId, pin, email }),
    onSuccess: (result, { pin }) => setConfirmed({ pin, result }),
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.validate()
    if (!form.isValid) return
    submitPin({ pin: form.pin.value ?? '', email: form.email.value ?? '' })
  }

  if (confirmed) {
    return (
      <PinSetupConfirmation
        pin={confirmed.pin}
        result={confirmed.result}
        onContinue={() => {
          setPin(instanceId, confirmed.pin)
          onDone?.()
        }}
      />
    )
  }

  // Validator messages are i18n keys (see `SurveyPinForm`).
  const errorFor = (field: typeof form.pin) => {
    const message = field.errors[0]?.message
    return message ? t(`surveyPin.errors.${message}`) : null
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      data-testid="pin-setup-form"
      noValidate
    >
      <div>
        <h2 className="text-lg font-medium text-text-primary">{t('surveyPin.title')}</h2>
        <p className="mt-1 text-sm text-text-secondary">{t('surveyPin.description')}</p>
      </div>
      <div
        className="rounded-md bg-accent-50 px-3 py-2 text-sm text-accent-700"
        data-testid="pin-setup-notice"
      >
        {t('surveyPin.notice')}
      </div>
      <Input
        type="email"
        autoComplete="email"
        label={t('surveyPin.emailLabel')}
        value={form.email.value ?? ''}
        onChange={(e) => createFormFieldChangeHandler(form.email)(e.target.value)}
        data-testid="pin-email-input"
      />
      {form.email.isTouched && errorFor(form.email) && (
        <ErrorMessage>{errorFor(form.email)}</ErrorMessage>
      )}
      <Input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        label={t('surveyPin.pinLabel')}
        value={form.pin.value ?? ''}
        onChange={(e) => createFormFieldChangeHandler(form.pin)(e.target.value)}
        data-testid="pin-input"
      />
      {form.pin.isTouched && errorFor(form.pin) && (
        <ErrorMessage>{errorFor(form.pin)}</ErrorMessage>
      )}
      <Input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        label={t('surveyPin.confirmLabel')}
        value={form.confirmPin.value ?? ''}
        onChange={(e) => createFormFieldChangeHandler(form.confirmPin)(e.target.value)}
        data-testid="pin-confirm-input"
      />
      {form.confirmPin.isTouched && errorFor(form.confirmPin) && (
        <ErrorMessage>{errorFor(form.confirmPin)}</ErrorMessage>
      )}
      {isError && <ErrorMessage>{t('surveyPin.errors.setupFailed')}</ErrorMessage>}
      <Button
        type="submit"
        isLoading={isPending}
        disabled={isPending || !form.isValid}
        data-testid="pin-submit"
      >
        {t('surveyPin.submit')}
      </Button>
    </form>
  )
}

/**
 * Asks the respondent for their email and the PIN that protects their survey
 * instance, then shows the PIN once. RAPA emails the PIN and resume link.
 */
export const PinSetupForm = (props: PinSetupFormProps) => (
  <FormProvider<SurveyPinFormInputs>
    formClass={SurveyPinForm}
    formLevelValidators={surveyPinFormLevelValidators}
  >
    <PinSetupFormInner {...props} />
  </FormProvider>
)
